// ─────────────────────────────────────────────────────────────────────────────
// AuthWidget.jsx
//
// Header widget for sign-in / account display.
//   - Signed out: a small "Sign in" button. Click → modal with email input.
//   - Signed in:  the user's email + a small dropdown for tier and sign-out.
//
// Self-contained: no props required. Reads tier/user via the hooks in
// userTier.js, calls auth.js for the actual sign-in/out actions.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from "react";
import { useCurrentUser, useUserTier, TIERS } from "./userTier";
import { sendMagicLink, signOut, completeMagicLinkSignInIfPresent } from "./auth";

const TIER_LABELS = {
  [TIERS.FREE]:   "Free",
  [TIERS.ARTIST]: "Artist",
  [TIERS.STUDIO]: "Studio",
};

export default function AuthWidget() {
  const user = useCurrentUser();
  const tier = useUserTier();

  const [modalOpen, setModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // On first mount, finish any magic-link sign-in that may be in the URL.
  useEffect(() => {
    completeMagicLinkSignInIfPresent().catch((err) => {
      console.warn("magic-link completion error:", err);
    });
  }, []);

  return (
    <>
      <div style={containerStyle}>
        {!user ? (
          <button
            onClick={() => setModalOpen(true)}
            style={signInButtonStyle}
            data-description="Sign in to your account. Required for saving as TIFF and other paid features. Uses a magic link sent to your email — no password needed."
          >
            Sign in
          </button>
        ) : (
          <AccountMenu
            email={user.email}
            tier={tier}
            open={menuOpen}
            onToggle={() => setMenuOpen((v) => !v)}
            onClose={() => setMenuOpen(false)}
          />
        )}
      </div>

      {modalOpen && <SignInModal onClose={() => setModalOpen(false)} />}
    </>
  );
}


// ─── Account menu (signed-in state) ─────────────────────────────────────────

function AccountMenu({ email, tier, open, onToggle, onClose }) {
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, onClose]);

  async function handleSignOut() {
    try {
      await signOut();
    } catch (err) {
      console.warn("sign-out failed:", err);
    } finally {
      onClose();
    }
  }

  return (
    <div style={{ position: "relative" }} ref={menuRef}>
      <button
        onClick={onToggle}
        style={accountButtonStyle}
        data-description={`Account menu. Signed in as ${email}. Current tier: ${TIER_LABELS[tier]}. Click to see options including sign out.`}
      >
        <span style={{ opacity: 0.8 }}>{email}</span>
        <span style={tierPillStyle}>{TIER_LABELS[tier]}</span>
      </button>

      {open && (
        <div style={menuStyle}>
          <div style={menuHeaderStyle}>
            <div style={{ fontSize: "13px", color: "#cbd5e1" }}>Signed in as</div>
            <div style={{ fontSize: "14px", fontWeight: 600 }}>{email}</div>
            <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: "8px" }}>
              Tier: <strong style={{ color: "#e2e8f0" }}>{TIER_LABELS[tier]}</strong>
            </div>
          </div>
          <button onClick={handleSignOut} style={menuItemStyle}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}


// ─── Sign-in modal ──────────────────────────────────────────────────────────

function SignInModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  // Close on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (sending) return;
    setError(null);
    setSending(true);
    try {
      await sendMagicLink(email.trim());
      setSent(true);
    } catch (err) {
      console.error("sendMagicLink failed:", err);
      setError(err?.message || "Could not send the sign-in link. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Sign in"
    >
      <div style={modalStyle}>
        <button onClick={onClose} style={closeButtonStyle} aria-label="Close">
          ×
        </button>

        {!sent ? (
          <>
            <h2 style={modalTitleStyle}>Sign in</h2>
            <p style={modalBodyStyle}>
              Enter your email and we'll send you a one-time sign-in link.
              No password needed.
            </p>
            <form onSubmit={handleSubmit}>
              <label htmlFor="auth-email" style={modalLabelStyle}>
                Email address
              </label>
              <input
                id="auth-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={modalInputStyle}
                disabled={sending}
              />

              {error && <div style={errorStyle}>{error}</div>}

              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={cancelButtonStyle}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={primaryButtonStyle}
                  disabled={sending || !email.trim()}
                >
                  {sending ? "Sending…" : "Send link"}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 style={modalTitleStyle}>Check your email</h2>
            <p style={modalBodyStyle}>
              We sent a sign-in link to <strong>{email}</strong>. Click the
              link in that email to finish signing in. You can close this
              window — when you click the link, it will open the app and
              sign you in automatically.
            </p>
            <p style={{ ...modalBodyStyle, fontSize: "13px", color: "#94a3b8" }}>
              Don't see it? Check your spam folder. The link expires after
              about an hour.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
              <button onClick={onClose} style={primaryButtonStyle}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ─── Styles ─────────────────────────────────────────────────────────────────

const containerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  padding: "8px 16px",
};

const signInButtonStyle = {
  background: "transparent",
  color: "#e2e8f0",
  border: "1px solid #475569",
  borderRadius: "6px",
  padding: "6px 14px",
  fontSize: "14px",
  cursor: "pointer",
  fontFamily: "inherit",
};

const accountButtonStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  background: "transparent",
  color: "#e2e8f0",
  border: "1px solid #334155",
  borderRadius: "6px",
  padding: "6px 12px",
  fontSize: "13px",
  cursor: "pointer",
  fontFamily: "inherit",
};

const tierPillStyle = {
  background: "#1e293b",
  color: "#60a5fa",
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  padding: "2px 8px",
  borderRadius: "10px",
};

const menuStyle = {
  position: "absolute",
  right: 0,
  top: "calc(100% + 6px)",
  minWidth: "260px",
  background: "#0f172a",
  border: "1px solid #334155",
  borderRadius: "8px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
  overflow: "hidden",
  zIndex: 1000,
};

const menuHeaderStyle = {
  padding: "14px 16px",
  borderBottom: "1px solid #1e293b",
  color: "#e2e8f0",
};

const menuItemStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "transparent",
  color: "#e2e8f0",
  border: "none",
  padding: "12px 16px",
  fontSize: "14px",
  cursor: "pointer",
  fontFamily: "inherit",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: "20px",
};

const modalStyle = {
  position: "relative",
  background: "#0f172a",
  border: "1px solid #334155",
  borderRadius: "12px",
  padding: "28px",
  maxWidth: "440px",
  width: "100%",
  color: "#e2e8f0",
};

const closeButtonStyle = {
  position: "absolute",
  top: "12px",
  right: "12px",
  background: "transparent",
  color: "#94a3b8",
  border: "none",
  fontSize: "24px",
  width: "32px",
  height: "32px",
  cursor: "pointer",
  fontFamily: "inherit",
  lineHeight: 1,
};

const modalTitleStyle = {
  fontSize: "22px",
  margin: "0 0 12px 0",
  fontWeight: 600,
};

const modalBodyStyle = {
  fontSize: "14px",
  color: "#cbd5e1",
  lineHeight: 1.5,
  margin: "0 0 16px 0",
};

const modalLabelStyle = {
  display: "block",
  fontSize: "12px",
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  marginBottom: "6px",
};

const modalInputStyle = {
  width: "100%",
  background: "#1e293b",
  color: "#e2e8f0",
  border: "1px solid #334155",
  borderRadius: "6px",
  padding: "10px 12px",
  fontSize: "15px",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const primaryButtonStyle = {
  background: "#60a5fa",
  color: "#0f172a",
  border: "none",
  borderRadius: "6px",
  padding: "10px 20px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const cancelButtonStyle = {
  background: "transparent",
  color: "#cbd5e1",
  border: "1px solid #334155",
  borderRadius: "6px",
  padding: "10px 20px",
  fontSize: "14px",
  cursor: "pointer",
  fontFamily: "inherit",
};

const errorStyle = {
  marginTop: "12px",
  padding: "10px 12px",
  background: "#7f1d1d",
  color: "#fee2e2",
  borderRadius: "6px",
  fontSize: "13px",
};
